import classes from "./styles.module.css";
import { Button, CircularProgress, TextField } from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import { path } from "../../../routes";
import { useEffect, useMemo, useState } from "react";
import Row_org_user from "./Row_org_user";
import AddUser_modal from "../Modal/AddUser_modal";
import EditUser_modal from "../Modal/EditUser_modal";
import YesNo_modal from "../Modal/YesNo_modal";

const GRID_USERS = "minmax(0, 1fr) minmax(0, 1fr) minmax(200px, auto)";

const SortableHeader = ({ column, children }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && column.toggleSorting(column.getIsSorted() === "asc")}
    style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
  >
    {children}
    {column.getIsSorted() === "asc" && " ↑"}
    {column.getIsSorted() === "desc" && " ↓"}
  </span>
);

const Users = () => {
  const [orgList, setOrgList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [sorting, setSorting] = useState([{ id: "username", desc: false }]);
  const [addNew, setAddNew] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const closeModal = () => {
    setAddNew(false);
    getUsers();
  };

  const closeEditModal = () => {
    setEditUser(null);
    getUsers();
  };

  const handleEditUser = (user) => setEditUser(user);

  const deleteUser = async (id) => {
    try {
      await axios.delete(`${path}/deleteuser/${id}`);
      alert("User smazán úspěšně");
      setDeleteConfirm(null);
      getUsers();
    } catch (error) {
      console.error("Chyba při mazání:", error);
      alert("Mazání se nezdařilo");
      setDeleteConfirm(null);
    }
  };

  const handleDeleteRequest = (user) => {
    setDeleteConfirm({ id: user.id, username: user.username });
  };

  const getUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${path}/getUsers`);
      setOrgList(response.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "username",
        accessorKey: "username",
        header: ({ column }) => <SortableHeader column={column}>Uživatel</SortableHeader>,
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          (rowA.original.username || "").localeCompare(rowB.original.username || "", "cs"),
      },
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => <SortableHeader column={column}>Organizace</SortableHeader>,
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
          (rowA.original.name || "").localeCompare(rowB.original.name || "", "cs"),
      },
      { id: "actions", header: "Action buttons", enableSorting: false },
    ],
    []
  );

  const table = useReactTable({
    data: orgList,
    columns,
    state: { sorting, globalFilter: filterName },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = (filterValue || "").trim().toLowerCase();
      if (!q) return true;
      const user = (row.original.username || "").toLowerCase();
      const org = (row.original.name || "").toLowerCase();
      return user.includes(q) || org.includes(q);
    },
  });

  const headerGroup = table.getHeaderGroups()[0];
  const rows = table.getRowModel().rows;
  const colCount = headerGroup?.headers.length ?? 0;

  return (
    <>
      {!addNew && (
        <div className={classes.main}>
          <div className={classes.buttons}>
            <Button
              onClick={() => setAddNew(true)}
              color="primary"
              variant="contained"
              sx={{
                width: { xs: "100%", sm: "auto" },
                fontSize: { xs: "0.875rem", sm: "1rem" },
                padding: { xs: "8px 16px", sm: "10px 20px" },
              }}
            >
              Přidat uživatele
            </Button>
            <TextField
              size="small"
              placeholder="Filtrovat podle jména nebo organizace..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              sx={{ minWidth: { xs: 160, sm: 260 } }}
              inputProps={{ "aria-label": "Filtrovat" }}
            />
          </div>
          <div className={classes.table}>
            <div
              className={classes.tableGridWrapper}
              style={{ gridTemplateColumns: GRID_USERS }}
            >
              <div style={{ display: "contents" }}>
                {headerGroup?.headers.map((header, i) => (
                  <div
                    key={header.id}
                    className={`${classes.cellHeader} ${i === 0 ? classes.cellColFirst : ""} ${i === colCount - 1 ? classes.cellColLast : ""} ${header.column.id === "actions" ? classes.columnAct : classes.column}`}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                ))}
              </div>
              {loading && (
                <div className={classes.loader} style={{ gridColumn: "1 / -1" }}>
                  <CircularProgress size={40} />
                </div>
              )}
              {!loading && orgList.length === 0 && (
                <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
                  Žádní uživatelé
                </div>
              )}
              {!loading && orgList.length > 0 && rows.length === 0 && (
                <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
                  Žádný výsledek pro „{filterName.trim()}“
                </div>
              )}
              {!loading &&
                rows.map((row, rowIndex) => (
                  <Row_org_user
                    key={row.original.id}
                    data={row.original}
                    onDeleteRequest={handleDeleteRequest}
                    onEdit={handleEditUser}
                    gridColumns={GRID_USERS}
                    renderAsGridRow
                    isLastRow={rowIndex === rows.length - 1}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
      {addNew && <AddUser_modal close={closeModal} />}
      {editUser && <EditUser_modal close={closeEditModal} user={editUser} />}
      {deleteConfirm && (
        <YesNo_modal
          text="Opravdu chcete odstranit tohoto uživatele?"
          line={deleteConfirm.username}
          id={deleteConfirm.id}
          close={() => setDeleteConfirm(null)}
          delete={deleteUser}
        />
      )}
    </>
  );
};

export default Users;
